import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    type StorefrontCustomerProfile,
    storefrontCustomerProfileValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/storefrontCustomerProfilesWorkspaceCapability/domain/StorefrontCustomerProfile";
import {
    CustomerProfileStatus,
    STOREFRONT_SYSTEM_ACTOR_ID,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/storefrontCustomerProfilesWorkspaceCapability/domain/StorefrontCustomerProfiles";
import {
    type OttokartCustomerTableAggregate,
    ottokartCustomerTableAggregateValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottokartCustomerEngagementWorkspaceCapability/domain/OttokartCustomerEngagementCustomerDetail";
import {
    type OttokartCustomerCategoryRequestReviewItem,
    type OttokartNewFleetMachineReviewItem,
    ottokartCustomerCategoryRequestReviewItemValidator,
    ottokartNewFleetMachineReviewItemValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottokartCustomerEngagementWorkspaceCapability/domain/OttokartCustomerEngagementReviewQueue";
import {plutoPost, type PlutoWorkspaceClientContext} from "~/utilities/ottokart/plutoClient";
import {inclusiveIsoDateTimeToExclusiveIsoDateTime} from "~/utilities/calendarDateIso";
import type {ZodType} from "zod";
import {z as zod} from "zod";

const PLUTO_PROFILES_PAGE_SIZE = 100;

// --- Types (CustomerModel) ---

export type Customer = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    email: string | null;
    phoneNumber: string;
    firstName: string | null;
    lastName: string | null;
    isVerified: boolean;
};

/** Enriched customers-table row (aggregates optional until enrich completes). */
export type CustomerListItem = Customer & {
    quotationCount?: number;
    /** Leads matched to this customer's phone number; undefined until the leads list loads. */
    enquiryCount?: number;
    machines?: Array<string>;
    categories?: Array<string>;
    partTypes?: Array<string>;
};

export type CustomerTableAggregate = OttokartCustomerTableAggregate;

// --- PostCustomer (create-customer body; id/createdAt/updatedAt set by backend) ---

export type PostCustomer = {
    createdBy: Uuid;
    updatedBy?: Uuid;
    email?: string | null;
    phoneNumber: string;
    firstName: string;
    lastName?: string | null;
    isVerified?: boolean;
};

// --- ListCustomersRequest ---

export type ListCustomersRequest = {
    page?: number;
    size?: number;
    sort?: string;
    isVerified?: boolean;
};

// --- Zod validators (core data only; robustJsonResultClientFetch unwraps success/data) ---

export const customerZodValidator: ZodType<Customer> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    email: zod.string().nullable(),
    phoneNumber: zod.string(),
    firstName: zod.string().nullable(),
    lastName: zod.string().nullable(),
    isVerified: zod.boolean(),
});

export const postCustomerZodValidator: ZodType<PostCustomer> = zod.object({
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator.optional(),
    email: zod.string().nullable().optional(),
    phoneNumber: zod.string(),
    firstName: zod.string(),
    lastName: zod.string().nullable().optional(),
    isVerified: zod.boolean().optional(),
});

export const listCustomersRequestZodValidator: ZodType<ListCustomersRequest> = zod.object({
    page: zod.number().optional(),
    size: zod.number().optional(),
    sort: zod.string().optional(),
});

function splitFullName(fullName: string | null | undefined): {firstName: string | null; lastName: string | null} {
    const trimmed = fullName?.trim() ?? "";
    if (trimmed.length === 0) {
        return {
            firstName: null,
            lastName: null,
        };
    }

    const spaceIndex = trimmed.indexOf(" ");
    if (spaceIndex === -1) {
        return {
            firstName: trimmed,
            lastName: null,
        };
    }

    return {
        firstName: trimmed.slice(0, spaceIndex).trim() || null,
        lastName: trimmed.slice(spaceIndex + 1).trim() || null,
    };
}

function mapStorefrontCustomerProfileToCustomer(profile: StorefrontCustomerProfile): Customer {
    const {firstName, lastName} = splitFullName(profile.fullName);

    return {
        id: profile.id,
        createdAt: profile.createdAt,
        updatedAt: profile.updatedAt,
        createdBy: STOREFRONT_SYSTEM_ACTOR_ID,
        updatedBy: STOREFRONT_SYSTEM_ACTOR_ID,
        email: profile.email ?? null,
        phoneNumber: profile.phone ?? "",
        firstName: firstName,
        lastName: lastName,
        isVerified: profile.status === CustomerProfileStatus.active,
    };
}

function normalizePhoneNumber(phoneNumber: string): string {
    return phoneNumber.trim();
}

export type CustomerColumnValues = {
    email: Array<string>;
    firstName: Array<string>;
    lastName: Array<string>;
    phoneNumber: Array<string>;
};

export type CustomerServerFilters = {
    createdAt: [string | null, string | null];
    email: Array<string> | null;
    firstName: Array<string> | null;
    lastName: Array<string> | null;
    phoneNumber: Array<string> | null;
    updatedAt: [string | null, string | null];
};

function customerFiltersBody(filters: CustomerServerFilters | undefined): {filters: Record<string, unknown>} | Record<string, never> {
    if (filters === undefined) {
        return {};
    }
    const requestFilters: {
        createdAtFrom?: string;
        createdAtTo?: string;
        emails?: Array<string>;
        firstNames?: Array<string>;
        lastNames?: Array<string>;
        phoneNumbers?: Array<string>;
        updatedAtFrom?: string;
        updatedAtTo?: string;
    } = {};
    if (filters.phoneNumber !== null) {
        requestFilters.phoneNumbers = filters.phoneNumber;
    }
    if (filters.firstName !== null) {
        requestFilters.firstNames = filters.firstName;
    }
    if (filters.lastName !== null) {
        requestFilters.lastNames = filters.lastName;
    }
    if (filters.email !== null) {
        requestFilters.emails = filters.email;
    }
    if (filters.createdAt[0] !== null && filters.createdAt[1] !== null) {
        requestFilters.createdAtFrom = filters.createdAt[0];
        requestFilters.createdAtTo = filters.createdAt[1];
    }
    if (filters.updatedAt[0] !== null && filters.updatedAt[1] !== null) {
        requestFilters.updatedAtFrom = filters.updatedAt[0];
        requestFilters.updatedAtTo = filters.updatedAt[1];
    }
    if (
        requestFilters.phoneNumbers === undefined &&
        requestFilters.firstNames === undefined &&
        requestFilters.lastNames === undefined &&
        requestFilters.emails === undefined &&
        requestFilters.createdAtFrom === undefined &&
        requestFilters.updatedAtFrom === undefined
    ) {
        return {};
    }
    return {
        filters: requestFilters,
    };
}

export function customerServerFiltersKey(filters: CustomerServerFilters): string {
    const phoneKey = filters.phoneNumber === null ? "*" : filters.phoneNumber.join("\0");
    const firstNameKey = filters.firstName === null ? "*" : filters.firstName.join("\0");
    const lastNameKey = filters.lastName === null ? "*" : filters.lastName.join("\0");
    const emailKey = filters.email === null ? "*" : filters.email.join("\0");
    return [phoneKey, firstNameKey, lastNameKey, emailKey, filters.createdAt.join("\0"), filters.updatedAt.join("\0")].join("|");
}

const customerColumnValuesValidator = zod.object({
    email: zod.array(zod.string()),
    firstName: zod.array(zod.string()),
    lastName: zod.array(zod.string()),
    phoneNumber: zod.array(zod.string()),
});

export async function listCustomerColumnValues({
    accessToken,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext): Promise<Result<CustomerColumnValues>> {
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/storefront-customer-profiles/get-all",
        body: {
            columnValuesOnly: true,
        },
        responseValidator: customerColumnValuesValidator,
    });
}

async function fetchStorefrontCustomerProfiles({
    accessToken,
    filters,
    listParams,
    pageSize: requestedPageSize,
    search,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    filters?: CustomerServerFilters;
    listParams?: ListCustomersRequest;
    pageSize?: number;
    search?: string;
}): Promise<Result<Array<StorefrontCustomerProfile>>> {
    const trimmedSearch = search?.trim() ?? "";
    const searchBody = trimmedSearch.length > 0 ? {search: trimmedSearch} : {};
    const filtersBody = customerFiltersBody(filters);
    const pageSize = listParams?.size ?? requestedPageSize ?? PLUTO_PROFILES_PAGE_SIZE;
    const page = listParams?.page ?? 0;
    const status = listParams?.isVerified === true ? CustomerProfileStatus.active : undefined;

    if (listParams?.page !== undefined || listParams?.size !== undefined) {
        const offset = page * pageSize;
        const pageResult = await plutoPost({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            workspaceId: workspaceId,
            path: "/storefront-customer-profiles/get-all",
            body: {
                limit: pageSize,
                offset: offset,
                ...(status !== undefined ? {status: status} : {}),
                ...searchBody,
                ...filtersBody,
            },
            responseValidator: zod.array(storefrontCustomerProfileValidator),
        });
        return pageResult;
    }

    const chunkSize = Math.max(1, requestedPageSize ?? PLUTO_PROFILES_PAGE_SIZE);
    const profiles: Array<StorefrontCustomerProfile> = [];
    let offset = 0;

    while (true) {
        const pageResult = await plutoPost({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            workspaceId: workspaceId,
            path: "/storefront-customer-profiles/get-all",
            body: {
                limit: chunkSize,
                offset: offset,
                ...(status !== undefined ? {status: status} : {}),
                ...searchBody,
                ...filtersBody,
            },
            responseValidator: zod.array(storefrontCustomerProfileValidator),
        });
        if (pageResult.success !== true) {
            return pageResult;
        }

        profiles.push(...pageResult.data);
        if (pageResult.data.length < chunkSize) {
            break;
        }
        offset += chunkSize;
    }

    return dataResult(profiles);
}

// --- API calls (Pluto storefront customer profiles) ---

export async function listCustomers({
    accessToken,
    filters,
    listParams,
    pageSize,
    search,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    filters?: CustomerServerFilters;
    listParams?: ListCustomersRequest;
    pageSize?: number;
    search?: string;
}): Promise<Result<Array<CustomerListItem>>> {
    const profilesResult = await fetchStorefrontCustomerProfiles({
        accessToken: accessToken,
        filters: filters,
        listParams: listParams,
        pageSize: pageSize,
        search: search,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (profilesResult.success !== true) {
        return profilesResult;
    }

    return dataResult(profilesResult.data.map(mapStorefrontCustomerProfileToCustomer));
}

export async function countCustomers({
    accessToken,
    filters,
    intellsysBaseUrl,
    search,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    filters?: CustomerServerFilters;
    search?: string;
}): Promise<Result<number>> {
    const trimmedSearch = search?.trim() ?? "";
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/storefront-customer-profiles/get-all",
        body: {
            countOnly: true,
            ...(trimmedSearch.length > 0 ? {search: trimmedSearch} : {}),
            ...customerFiltersBody(filters),
        },
        responseValidator: zod.coerce.number().int().nonnegative(),
    });
}

export async function listCustomerTableAggregates({
    accessToken,
    customerIds,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    customerIds: Array<Uuid>;
}): Promise<Result<Array<CustomerTableAggregate>>> {
    if (customerIds.length === 0) {
        return dataResult([]);
    }

    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/ottokart-customer-engagement/customers/list-table-aggregates",
        body: {
            customerIds: customerIds,
        },
        responseValidator: zod.array(ottokartCustomerTableAggregateValidator),
    });
}

export async function getCustomersAnalyticsSummary({
    accessToken,
    from,
    intellsysBaseUrl,
    to,
    workspaceId,
}: PlutoWorkspaceClientContext & {from: Iso8601DateTime; to: Iso8601DateTime}): Promise<Result<{totalVerifiedCustomers: number}>> {
    const toExclusive = inclusiveIsoDateTimeToExclusiveIsoDateTime(to);
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/storefront-customer-profiles/analytics/summary",
        body: {from: from, to: toExclusive},
        responseValidator: zod.object({
            totalVerifiedCustomers: zod.number(),
        }),
    });
}

export async function getCustomersAnalyticsTrend({
    accessToken,
    from,
    intellsysBaseUrl,
    to,
    workspaceId,
}: PlutoWorkspaceClientContext & {from: Iso8601DateTime; to: Iso8601DateTime}): Promise<Result<Array<{month: string; count: number}>>> {
    const toExclusive = inclusiveIsoDateTimeToExclusiveIsoDateTime(to);
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/storefront-customer-profiles/analytics/trend",
        body: {from: from, to: toExclusive},
        responseValidator: zod.array(zod.object({
            month: zod.string(),
            count: zod.number(),
        })),
    });
}

export async function getCustomersAnalyticsTrendDaily({
    accessToken,
    from,
    intellsysBaseUrl,
    to,
    workspaceId,
}: PlutoWorkspaceClientContext & {from: Iso8601DateTime; to: Iso8601DateTime}): Promise<Result<Array<{date: string; count: number}>>> {
    const toExclusive = inclusiveIsoDateTimeToExclusiveIsoDateTime(to);
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/storefront-customer-profiles/analytics/trend/daily",
        body: {
            from: from,
            to: toExclusive,
        },
        responseValidator: zod.array(zod.object({
            date: zod.string(),
            count: zod.number(),
        })),
    });
}

export async function getCustomerByPhone({
    accessToken,
    phoneNumber,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    phoneNumber: string;
}): Promise<Result<Customer | null>> {
    const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
    const profilesResult = await fetchStorefrontCustomerProfiles({
        accessToken: accessToken,
        listParams: {
            isVerified: true,
        },
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (profilesResult.success !== true) {
        return profilesResult;
    }

    const matchingProfile = profilesResult.data.find((profile) => {
        return normalizePhoneNumber(profile.phone ?? "") === normalizedPhoneNumber;
    });
    if (matchingProfile === undefined) {
        return dataResult(null);
    }

    return dataResult(mapStorefrontCustomerProfileToCustomer(matchingProfile));
}

export async function createCustomer({
    accessToken,
    customer,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    customer: PostCustomer;
}): Promise<Result<Uuid>> {
    const fullName = `${customer.firstName}${customer.lastName !== null && customer.lastName !== undefined && customer.lastName.length > 0 ? ` ${customer.lastName}` : ""}`.trim();

    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/storefront-customer-profiles/create",
        body: {
            email: customer.email ?? null,
            fullName: fullName.length > 0 ? fullName : customer.firstName,
            phone: customer.phoneNumber,
        },
        responseValidator: uuidZodValidator,
    });
}

type BulkUploadCustomerRow = {
    row: number;
    firstName: string;
    lastName: string;
    phoneNumber: string;
};

export type BulkUploadCustomersFailure = {
    row: number;
    phone: string;
    reason: string;
};

export type BulkUploadCustomersSummary = {
    created: number;
    skippedDuplicates: number;
    failed: Array<BulkUploadCustomersFailure>;
};

function parseBulkUploadCustomersCsv(fileText: string): Result<Array<BulkUploadCustomerRow>> {
    const lines = fileText
        .split(/\r?\n/)
        .map((line) => {
            return line.trim();
        })
        .filter((line) => {
            return line.length > 0;
        });

    if (lines.length < 2) {
        return errorResult(new Error("CSV must include a header row and at least one customer row."));
    }

    const headerLine = lines[0];
    if (headerLine === undefined) {
        return errorResult(new Error("CSV must include a header row and at least one customer row."));
    }

    const headerColumns = headerLine.split(",").map((column) => {
        return column.trim();
    });
    const headerName = headerColumns[0];
    const headerPhone = headerColumns[1];
    if (
        headerColumns.length !== 2
        || headerName === undefined
        || headerPhone === undefined
        || headerName.toLowerCase() !== "name"
        || headerPhone.toLowerCase() !== "phone number"
    ) {
        return errorResult(new Error("CSV must have exactly two headers: Name, Phone number"));
    }

    const rows: Array<BulkUploadCustomerRow> = [];

    for (let lineIndex = 1; lineIndex < lines.length; lineIndex++) {
        const lineNumber = lineIndex + 1;
        const line = lines[lineIndex];
        if (line === undefined) {
            continue;
        }
        const columns = line.split(",").map((column) => {
            return column.trim();
        });
        const name = columns[0] ?? "";
        const phoneNumber = columns[1] ?? "";

        if (name.length === 0) {
            return errorResult(new Error(`Name is required at line ${lineNumber}`));
        }
        if (phoneNumber.length === 0) {
            return errorResult(new Error(`Phone number is required at line ${lineNumber}`));
        }

        const spaceIndex = name.indexOf(" ");
        let firstName: string;
        let lastName: string;
        if (spaceIndex === -1) {
            firstName = name;
            lastName = name;
        } else {
            firstName = name.slice(0, spaceIndex).trim();
            lastName = name.slice(spaceIndex + 1).trim();
            if (lastName.length === 0) {
                lastName = firstName;
            }
        }

        rows.push({
            row: lineNumber,
            firstName: firstName,
            lastName: lastName,
            phoneNumber: phoneNumber,
        });
    }

    return dataResult(rows);
}

/**
 * FE-only interim bulk upload.
 *
 * Duplicate checks use `/storefront-customer-profiles/get-all` without a status filter
 * so all profiles returned by Pluto are considered (verified and unverified).
 * In-file phone duplicates are also skipped after the first occurrence.
 */
export async function bulkUploadCustomers({
    accessToken,
    file,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    file: File;
}): Promise<Result<BulkUploadCustomersSummary>> {
    const fileText = await file.text();
    const parsedRowsResult = parseBulkUploadCustomersCsv(fileText);
    if (parsedRowsResult.success !== true) {
        return parsedRowsResult;
    }

    // Omit isVerified so get-all is not filtered to active/verified profiles only.
    const existingProfilesResult = await fetchStorefrontCustomerProfiles({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (existingProfilesResult.success !== true) {
        return existingProfilesResult;
    }

    const existingPhoneNumbers = new Set(
        existingProfilesResult.data
            .map((profile) => {
                return normalizePhoneNumber(profile.phone ?? "");
            })
            .filter((phoneNumber) => {
                return phoneNumber.length > 0;
            }),
    );
    const phonesSeenInFile = new Set<string>();
    let created = 0;
    let skippedDuplicates = 0;
    const failed: Array<BulkUploadCustomersFailure> = [];

    for (const row of parsedRowsResult.data) {
        const normalizedPhoneNumber = normalizePhoneNumber(row.phoneNumber);

        if (existingPhoneNumbers.has(normalizedPhoneNumber) || phonesSeenInFile.has(normalizedPhoneNumber)) {
            skippedDuplicates += 1;
            phonesSeenInFile.add(normalizedPhoneNumber);
            continue;
        }

        phonesSeenInFile.add(normalizedPhoneNumber);

        const createResult = await createCustomer({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            workspaceId: workspaceId,
            customer: {
                createdBy: STOREFRONT_SYSTEM_ACTOR_ID,
                phoneNumber: row.phoneNumber,
                email: `${row.phoneNumber}@bulk-upload.local`,
                firstName: row.firstName,
                lastName: row.lastName,
                isVerified: false,
            },
        });
        if (createResult.success !== true) {
            failed.push({
                row: row.row,
                phone: row.phoneNumber,
                reason: createResult.error.message,
            });
            continue;
        }

        created += 1;
        existingPhoneNumbers.add(normalizedPhoneNumber);
    }

    return dataResult({
        created: created,
        skippedDuplicates: skippedDuplicates,
        failed: failed,
    });
}

/** Customer-submitted category request shown in Products → Review Queue. */
export type CustomerCategoryRequest = OttokartCustomerCategoryRequestReviewItem;

export async function listAllCustomerCategories({
    accessToken,
    intellsysBaseUrl,
    limit,
    offset,
    search,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    limit?: number;
    offset?: number;
    search?: string;
}): Promise<Result<Array<CustomerCategoryRequest>>> {
    const trimmedSearch = search?.trim() ?? "";
    const searchBody = trimmedSearch.length > 0 ? {search: trimmedSearch} : {};
    return await plutoPost({
        accessToken: accessToken,
        body: limit !== undefined && offset !== undefined
            ? {
                  limit: limit,
                  offset: offset,
                  ...searchBody,
              }
            : searchBody,
        path: "/ottokart-customer-engagement/category-requests/get-all",
        intellsysBaseUrl: intellsysBaseUrl,
        responseValidator: zod.array(ottokartCustomerCategoryRequestReviewItemValidator),
        workspaceId: workspaceId,
    });
}

/** New fleet machine request shown in Machines → Review Queue. */
export type NewFleetMachineRequest = OttokartNewFleetMachineReviewItem;

export async function countCustomerCategoryRequests({
    accessToken,
    intellsysBaseUrl,
    search,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    search?: string;
}): Promise<Result<number>> {
    const trimmedSearch = search?.trim() ?? "";
    return plutoPost({
        accessToken: accessToken,
        body: {
            countOnly: true,
            ...(trimmedSearch.length > 0 ? {search: trimmedSearch} : {}),
        },
        intellsysBaseUrl: intellsysBaseUrl,
        path: "/ottokart-customer-engagement/category-requests/get-all",
        responseValidator: zod.coerce.number().int().nonnegative(),
        workspaceId: workspaceId,
    });
}

export async function getNewFleetMachines({
    accessToken,
    intellsysBaseUrl,
    limit,
    offset,
    search,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    limit?: number;
    offset?: number;
    search?: string;
}): Promise<Result<Array<NewFleetMachineRequest>>> {
    const trimmedSearch = search?.trim() ?? "";
    const searchBody = trimmedSearch.length > 0 ? {search: trimmedSearch} : {};
    return await plutoPost({
        accessToken: accessToken,
        body: limit !== undefined && offset !== undefined
            ? {
                  limit: limit,
                  offset: offset,
                  ...searchBody,
              }
            : searchBody,
        path: "/ottokart-customer-engagement/fleet-profiles/get-new",
        intellsysBaseUrl: intellsysBaseUrl,
        responseValidator: zod.array(ottokartNewFleetMachineReviewItemValidator),
        workspaceId: workspaceId,
    });
}

export async function countNewFleetMachines({
    accessToken,
    intellsysBaseUrl,
    search,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    search?: string;
}): Promise<Result<number>> {
    const trimmedSearch = search?.trim() ?? "";
    return plutoPost({
        accessToken: accessToken,
        body: {
            countOnly: true,
            ...(trimmedSearch.length > 0 ? {search: trimmedSearch} : {}),
        },
        intellsysBaseUrl: intellsysBaseUrl,
        path: "/ottokart-customer-engagement/fleet-profiles/get-new",
        responseValidator: zod.coerce.number().int().nonnegative(),
        workspaceId: workspaceId,
    });
}

export async function deleteCustomerCategoryRequest({
    accessToken,
    customerId,
    id,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    customerId: Uuid;
    id: Uuid;
}): Promise<Result<null>> {
    return await plutoPost({
        accessToken: accessToken,
        body: {
            customerId: customerId,
            id: id,
        },
        path: "/ottokart-customer-engagement/category-requests/delete",
        intellsysBaseUrl: intellsysBaseUrl,
        responseValidator: zod.null(),
        workspaceId: workspaceId,
    });
}

export async function approveNewFleetMachine({
    accessToken,
    id,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    id: Uuid;
}): Promise<Result<null>> {
    return await plutoPost({
        accessToken: accessToken,
        body: {
            id: id,
        },
        path: "/ottokart-customer-engagement/fleet-profiles/approve",
        intellsysBaseUrl: intellsysBaseUrl,
        responseValidator: zod.null(),
        workspaceId: workspaceId,
    });
}

export async function rejectNewFleetMachine({
    accessToken,
    id,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    id: Uuid;
}): Promise<Result<null>> {
    return await plutoPost({
        accessToken: accessToken,
        body: {
            id: id,
        },
        path: "/ottokart-customer-engagement/fleet-profiles/reject",
        intellsysBaseUrl: intellsysBaseUrl,
        responseValidator: zod.null(),
        workspaceId: workspaceId,
    });
}
